/**
 * (c) 2021, Nous Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  Box,
  Divider,
  List,
  ListItem,
  HStack,
  VStack,
  Text,
  Stack,
  BoxProps
} from "@chakra-ui/layout";
import { IconButton } from "@chakra-ui/button";
import { Link, useMediaQuery } from "@chakra-ui/react";
import { ReactNode, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import AreaHeading from "../../common/AreaHeading";
import HeadedScrollablePanel from "../../common/HeadedScrollablePanel";
import { Anchor, useRouterTabSlug, useRouterState } from "../../router-hooks";
import { useAnimationDirection } from "../common/documentation-animation-hooks";
// import { RiArrowDownLine } from "react-icons/ri";
// import { RiDeleteBin5Line } from "react-icons/ri";
import { heightMd, widthXl } from "../../common/media-queries";
import DocString from "../common/DocString";
import DocumentationBreadcrumbHeading from "../common/DocumentationBreadcrumbHeading";
import { resolveModule } from "../api/apidocs-util";
import ApiNode from "../api/ApiNode";
import { splitDocString } from "../../editor/codemirror/language-server/docstrings";
import { ExtensionsDocsEntry, ExtensionsDocsResponse } from "../../language-server/extensionsdocs";
import sortBy from "lodash.sortby";
import DocumentationTopLevelItem from "../common/DocumentationTopLevelItem";
import { allowWrapAtPeriods } from "../common/wrap";
import { docStyles } from "../../common/documentation-styles";

interface ApiDocumentationProps {
  extensions: ExtensionsDocsResponse;
}

export const ExtensionsDocumentation = ({
  extensions
}: ApiDocumentationProps) => {
  const [anchor, setAnchor] = useRouterTabSlug("extensions");
  const handleNavigate = useCallback(
    (id: string | undefined) => {
      setAnchor(id ? { id } : undefined, "documentation-user");
    },
    [setAnchor]
  );
  const direction = useAnimationDirection(anchor);
  // console.log(anchor, extensions, direction, 'direction')
  return (
    <ActiveLevel
      key={anchor ? 0 : 1}
      anchor={anchor}
      onNavigate={handleNavigate}
      extensions={extensions}
      direction={direction}
    />
  );
};

interface ActiveLevelProps {
  anchor: Anchor | undefined;
  extensions: ExtensionsDocsResponse;
  onNavigate: (state: string | undefined) => void;
  direction: "forward" | "back" | "none";
}

const ActiveLevel = ({
  anchor,
  onNavigate,
  extensions,
  direction
}: ActiveLevelProps) => {
  const intl = useIntl();
  const [isShortWindow] = useMediaQuery(heightMd);
  const [isWideScreen] = useMediaQuery(widthXl);
  const extensionString = intl.formatMessage({ id: "extensions-tab" });
  // console.log('ActiveLevel extensions', extensions, anchor);
  const module = anchor ? resolveModule(extensions, anchor.id) : undefined;
  // console.log('module', extensions);

  const [, setParams] = useRouterState();

  const handleReferenceLink = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setParams({ tab: "reference" });
    },
    [setParams]
  );

  // const modulesList = useSelector((state: RootState) => state.apiStore.modulesList);

  // const dispatch = useDispatch()

  // 判断当前扩展api是否已经添加
  // const isAddState = useCallback(
  //   (item: object) => {
  //     const useShow = modulesList.some(i => { return i === item['extensionId']})
  //     return useShow
  //   }, [modulesList]
  // )

  // const handledownload = useCallback(
  //   async (item: object) => {
  //     let keyList = [ ...modulesList ]
  //     const useShow = modulesList.some(i => { return i === item['extensionId']})
  //     if (useShow) {
  //       console.log('当前扩展api已存在使用')
  //     } else {
  //       // console.log(keyList, 'keyListkeyList')
  //       keyList.push(item['extensionId'])
  //       dispatch(setModules(keyList))
  //       dispatch(setModulesType('add'))
  //     }
  //   }, [modulesList]
  // );

  // // 删除操作
  // const handleDelete = useCallback(
  //   async (item: object) => {
  //     let keyList = [ ...modulesList ]
  //     keyList = keyList.filter(i => i !== item['extensionId'])
  //     dispatch(setModules(keyList))
  //     dispatch(setModulesType('delete'))
  //   }, [modulesList]
  // )
  // 打开扩展中心得路由
  // const [anchor, setAnchor] = useRouterTabSlug("extensions");
  // const handleOpenLink = useCallback(
  //   async (item: object) => {
  //     setAnchor(item['extensionId'] ? item['extensionId'] : undefined, "documentation-user");
  //     // const urlParams = `/python/api/${item['extensionId']}`
  //     // console.log(window.location.origin, urlParams, 9999)
  //     // window.location.href = urlParams;
  //     // location.href=`${window.location.origin}${urlParams}`
  //     // window.open('https://www.matatalab.com', '_blank');
  //   }, []
  // )
  if (module) {
    return (
      <HeadedScrollablePanel
        direction={direction}
        heading={
          <DocumentationBreadcrumbHeading
            parent={extensionString}
            title={module['name']}
            onBack={() => onNavigate(undefined)}
            subtitle={<ShortModuleDescription value={module} as="span" />}
          />
        }
      >
        <List flex="1 1 auto">
          {/* {console.log('moudle.children', module)} */}
          {(module.children ?? []).map((child) => (
            <ListItem key={child.id}>
              <ApiNode
                docs={child}
                width="100%"
                anchor={anchor}
                sx={{
                  ...docStyles,
                }}
              />
              <Divider />
            </ListItem>
          ))}
        </List>
      </HeadedScrollablePanel>
    );
  }
  return (
    <HeadedScrollablePanel
      direction={direction}
      heading={
        <AreaHeading
          name={extensionString}
          description={
            <FormattedMessage
              id="extensions-description"
              values={{
                link: (chunks: ReactNode) => (
                  <Link color="brand.500" onClick={handleReferenceLink} href="">
                    {chunks}
                  </Link>
                ),
              }as any}
            />
          }
        />
      }
    >
      <List flex="1 1 auto" m={3}>
      {sortBy(Object.values(extensions), (m) => m.fullName).map((module) => (
          // <ListItem key={index}>
          //   <HStack
          //     my={3}
          //     mr={3}
          //     ml={5}
          //     spacing={isShortWindow || !isWideScreen ? 3 : 5}
          //   >
          //     <VStack
          //       alignItems="stretch"
          //       spacing={isShortWindow || !isWideScreen ? 0 : 1}
          //       flex="1 1 auto"
          //       style={{cursor: 'pointer'}}
          //       // onClick={() => { handleOpenLink(module) }}
          //       onClick={() => onNavigate(module['extensionId'])}
          //     >
          //       {/* 图片 */}
          //       <img src={module.src} alt="" />
          //       <HStack justifyContent="space-between">
          //         {/* 标题和描述 */}
          //         <Stack alignItems="start">
          //           <Text as="h3" fontSize="lg" fontWeight="semibold">
          //             {module.name}
          //           </Text>
          //           <Box fontSize="sm" noOfLines={1}>
          //             {module.desc}
          //           </Box>
          //         </Stack>
          //         {/* 删除按钮 */}
          //         {/* {
          //           modulesList && isAddState(module) ? 
          //             <IconButton
          //               icon={<RiDeleteBin5Line />}
          //               aria-label={module.name}
          //               size="sm"
          //               color="brand.800"
          //               variant="ghost"
          //               fontSize="2xl"
          //               onClick={() => { handleDelete(module) }}
          //             /> :
          //              // 下载按钮
          //             <IconButton
          //               icon={<RiArrowDownLine />}
          //               aria-label={module.name}
          //               size="sm"
          //               color="brand.800"
          //               variant="ghost"
          //               fontSize="2xl"
          //               onClick={() => { handledownload(module) }}
          //           />
          //         } */}
          //       </HStack>
          //     </VStack>
          //   </HStack>
          //   <Divider ml={3} borderWidth="1px" />
          // </ListItem>
          <DocumentationTopLevelItem
            key={module.id}
            name={allowWrapAtPeriods(module.fullName)}
            description={<ShortModuleDescription value={module} />}
            onForward={() => onNavigate(module.id)}
            type="api"
          />
      ))}
      </List>
    </HeadedScrollablePanel>
  );
};


interface ShortModuleDescriptionProps extends BoxProps {
  value: ExtensionsDocsEntry;
}
const ShortModuleDescription = ({
  value,
  ...props
}: ShortModuleDescriptionProps) =>
  value.docString ? (
    <DocString
      value={splitDocString(value.docString).summary.trim().replace(/\.$/, "")}
      {...props}
    />
  ) : null;

